/**
 * Réactions natives des exports Meta (Instagram / Messenger).
 *
 * Contrairement aux SMS (voir tapbackReactions.js), les réactions Meta ne sont PAS
 * recopiées comme pseudo-messages : elles vivent directement sur le message cible,
 * dans un champ `reactions[]` du JSON d'export :
 *
 *   {
 *     "content": "Coucou",
 *     "reactions": [{ "reaction": "❤", "actor": "Emmanuel Lorent" }]
 *   }
 *
 * En parallèle, l'export contient aussi des pseudo-messages « Reacted 😄 to your message »
 * / « Liked a message » (en anglais même sur une conversation FR) qui sont, eux, filtrés
 * à l'import (SYSTEM_MESSAGES des providers). Le champ `reactions[]` est la seule source
 * fiable ; on le sérialise dans le CSV pour survivre au pipeline JSON → CSV → JSON.
 *
 * Format interne aligné sur tapbackReactions.js : { emoji, sender }.
 */

// Décodage identique aux providers Meta : le JSON stocke les emojis en mojibake
// (ex. "\u00e2\u009d\u00a4" pour ❤). decodeURIComponent(escape()) restaure l'UTF-8.
function decodeMetaText(text) {
	if(text == null || text === '') {
		return '';
	}
	try {
		return decodeURIComponent(escape(`${text}`));
	} catch (err) {
		return `${text}`;
	}
}

// Meta exporte parfois des symboles Unicode « texte » sans sélecteur de présentation (U+FE0F).
// Ex. le « Like » = U+2764 (❤) au lieu de ❤️ — on force le rendu emoji pour l'aperçu/PDF.
function toEmojiPresentation(emoji) {
	if(!emoji || emoji.includes('\uFE0F')) {
		return emoji;
	}
	// Cœur « Like » Meta (seul cas observé dans les exports IG/Messenger).
	if(emoji === '\u2764') {
		return '\u2764\uFE0F';
	}
	return emoji;
}

/**
 * Extrait les réactions d'un message Meta au format interne { emoji, sender }.
 * Retourne [] si le message n'a pas de réactions.
 */
export function normalizeMetaReactions(message) {
	const reactions = message && message.reactions;
	if(!Array.isArray(reactions) || !reactions.length) {
		return [];
	}
	return reactions
		.map(reaction => {
			const emoji = toEmojiPresentation(decodeMetaText(reaction && reaction.reaction));
			const sender = decodeMetaText(reaction && reaction.actor);
			return emoji ? { emoji, sender } : null;
		})
		.filter(Boolean);
}

/**
 * Sérialise les réactions pour la colonne CSV (URL-encodé, comme le texte des messages).
 * L'encodage garantit l'absence de `;` et `\n` parasites dans la colonne.
 * Retourne '' si aucune réaction (aucune colonne ajoutée → rétrocompatibilité CSV).
 */
export function serializeReactionsForCsv(reactions) {
	if(!Array.isArray(reactions) || !reactions.length) {
		return '';
	}
	try {
		return encodeURIComponent(JSON.stringify(reactions));
	} catch (err) {
		return '';
	}
}

/**
 * Sérialise directement les réactions d'un message Meta pour le CSV (helper de commodité).
 */
export function getMessageReactionsCsv(message) {
	return serializeReactionsForCsv(normalizeMetaReactions(message));
}

/**
 * Parse la colonne CSV des réactions → tableau [{ emoji, sender }].
 * Tolérant : toute valeur invalide renvoie [].
 */
export function parseReactionsFromCsv(encoded) {
	if(!encoded) {
		return [];
	}
	try {
		const parsed = JSON.parse(decodeURIComponent(encoded));
		return Array.isArray(parsed) ? parsed.filter(reaction => reaction && reaction.emoji) : [];
	} catch (err) {
		return [];
	}
}

// Une colonne CSV encodée qui commence par %5B (« [ » encodé) est une liste de réactions.
// Sert à distinguer la colonne réactions d'une éventuelle colonne formatsLinesCount (« a5:… »).
export function isEncodedReactionsColumn(part) {
	return typeof part === 'string' && (part.startsWith('%5B') || part.startsWith('['));
}
